import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ProjectModal = ({ project, isOpen, onClose, mode = 'details' }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  if (!project) return null;

  const tabs = [
    { key: 'overview', label: 'Overview', icon: 'Eye' },
    { key: 'technical', label: 'Technical Deep Dive', icon: 'Code' },
    { key: 'metrics', label: 'Performance Metrics', icon: 'BarChart3' },
    { key: 'testimonial', label: 'Client Impact', icon: 'MessageSquare' }
  ];

  const nextImage = () => {
    setCurrentImageIndex((prev) => 
      prev === project.gallery.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? project.gallery.length - 1 : prev - 1
    );
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-background/95 backdrop-blur-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Modal Content */}
          <motion.div
            className="relative w-full max-w-6xl max-h-[90vh] overflow-hidden glass-morphism rounded-2xl cyber-border"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-brand-cyan to-brand-purple rounded-lg flex items-center justify-center cyber-glow">
                  <Icon name="Layers" size={24} color="#ffffff" />
                </div>
                <div>
                  <h2 className="font-exo text-2xl font-bold text-foreground">
                    {project.title}
                  </h2>
                  <p className="text-text-secondary">{project.category}</p>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="ExternalLink"
                  iconPosition="left"
                  className="cyber-border hover:cyber-glow"
                >
                  Live Demo
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="X"
                  onClick={onClose}
                  className="hover:bg-surface/50"
                />
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="flex space-x-1 p-6 pb-0">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-inter font-medium text-sm transition-all duration-300 ${
                    activeTab === tab.key
                      ? 'bg-brand-cyan/20 text-brand-cyan cyber-border' :'text-text-secondary hover:text-foreground hover:bg-surface/30'
                  }`}
                >
                  <Icon name={tab.icon} size={16} />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* Image Gallery */}
                  <div className="relative h-80 rounded-xl overflow-hidden cyber-border">
                    <Image
                      src={project.gallery[currentImageIndex]}
                      alt={`${project.title} screenshot ${currentImageIndex + 1}`}
                      className="w-full h-full object-cover"
                    />
                    
                    {project.gallery.length > 1 && (
                      <>
                        <button
                          onClick={prevImage}
                          className="absolute left-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-background/80 rounded-full flex items-center justify-center cyber-border hover:cyber-glow transition-all duration-300"
                        >
                          <Icon name="ChevronLeft" size={20} />
                        </button>
                        <button
                          onClick={nextImage}
                          className="absolute right-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-background/80 rounded-full flex items-center justify-center cyber-border hover:cyber-glow transition-all duration-300"
                        >
                          <Icon name="ChevronRight" size={20} />
                        </button>
                        
                        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                          {project.gallery.map((_, index) => (
                            <button
                              key={index}
                              onClick={() => setCurrentImageIndex(index)}
                              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                                index === currentImageIndex ? 'bg-brand-cyan' : 'bg-text-tertiary'
                              }`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Project Description */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div>
                      <h3 className="font-exo text-lg font-bold text-foreground mb-3">
                        Project Overview
                      </h3>
                      <p className="text-text-secondary leading-relaxed mb-4">
                        {project.fullDescription}
                      </p>
                      
                      <div className="space-y-3">
                        <div className="flex items-center space-x-3">
                          <Icon name="Calendar" size={16} className="text-brand-cyan" />
                          <span className="text-text-secondary">
                            Duration: {project.duration}
                          </span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <Icon name="Users" size={16} className="text-brand-cyan" />
                          <span className="text-text-secondary">
                            Team Size: {project.teamSize}
                          </span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <Icon name="Target" size={16} className="text-brand-cyan" />
                          <span className="text-text-secondary">
                            Role: {project.role}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="font-exo text-lg font-bold text-foreground mb-3">
                        Technology Stack
                      </h3>
                      <div className="grid grid-cols-2 gap-3">
                        {project.technologies.map((tech, index) => (
                          <div
                            key={index}
                            className="flex items-center space-x-2 p-3 bg-surface/30 rounded-lg cyber-border"
                          >
                            <div className="w-2 h-2 bg-brand-cyan rounded-full animate-pulse" />
                            <span className="text-foreground font-mono text-sm">{tech}</span>
                          </div>
                        ))}
                      </div>

                      <h3 className="font-exo text-lg font-bold text-foreground mb-3 mt-6">
                        Key Features
                      </h3>
                      <ul className="space-y-2">
                        {project.features.map((feature, index) => (
                          <li key={index} className="flex items-start space-x-2">
                            <Icon name="Check" size={16} className="text-brand-green mt-0.5" />
                            <span className="text-text-secondary">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'technical' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div>
                      <h3 className="font-exo text-lg font-bold text-foreground mb-3">
                        Architecture Overview
                      </h3>
                      <div className="bg-surface/30 rounded-lg p-4 cyber-border">
                        <pre className="text-brand-cyan font-mono text-sm overflow-x-auto">
                          {project.architecture}
                        </pre>
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="font-exo text-lg font-bold text-foreground mb-3">
                        Challenges & Solutions
                      </h3>
                      <div className="space-y-4">
                        {project.challenges.map((challenge, index) => (
                          <div key={index} className="bg-surface/30 rounded-lg p-4 cyber-border">
                            <h4 className="font-inter font-medium text-brand-orange mb-2">
                              Challenge: {challenge.problem}
                            </h4>
                            <p className="text-text-secondary text-sm">
                              Solution: {challenge.solution}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'metrics' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {Object.entries(project.detailedMetrics).map(([key, value]) => (
                      <div key={key} className="bg-surface/30 rounded-lg p-6 cyber-border text-center">
                        <div className="text-3xl font-mono font-bold text-brand-cyan mb-2">
                          {value.value}
                        </div>
                        <div className="text-foreground font-medium mb-1">
                          {value.label}
                        </div>
                        <div className="text-text-secondary text-sm">
                          {value.description}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'testimonial' && (
                <div className="space-y-6">
                  <div className="bg-surface/30 rounded-lg p-6 cyber-border">
                    <div className="flex items-start space-x-4">
                      <Image
                        src={project.testimonial.avatar}
                        alt={project.testimonial.name}
                        className="w-16 h-16 rounded-full cyber-border"
                      />
                      <div className="flex-1">
                        <blockquote className="text-foreground text-lg italic mb-4">
                          "{project.testimonial.quote}"
                        </blockquote>
                        <div>
                          <div className="font-inter font-medium text-brand-cyan">
                            {project.testimonial.name}
                          </div>
                          <div className="text-text-secondary text-sm">
                            {project.testimonial.position}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="font-exo text-lg font-bold text-foreground mb-3">
                        Business Impact
                      </h3>
                      <ul className="space-y-2">
                        {project.businessImpact.map((impact, index) => (
                          <li key={index} className="flex items-start space-x-2">
                            <Icon name="TrendingUp" size={16} className="text-brand-green mt-0.5" />
                            <span className="text-text-secondary">{impact}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div>
                      <h3 className="font-exo text-lg font-bold text-foreground mb-3">
                        Technical Achievements
                      </h3>
                      <ul className="space-y-2">
                        {project.technicalAchievements.map((achievement, index) => (
                          <li key={index} className="flex items-start space-x-2">
                            <Icon name="Zap" size={16} className="text-brand-cyan mt-0.5" />
                            <span className="text-text-secondary">{achievement}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProjectModal;